import { useRoute } from 'vue-router';
import { usePersonStore } from '~/stores/person';
import { watch } from 'vue';

type CrispCommand =
    | ['safe', boolean]
    | ['set', 'user:email', string]
    | ['do', 'chat:hide' | 'chat:show' | 'chat:open' | 'chat:close'];

interface CrispClient {
    push(command: CrispCommand): void;
}

declare global {
    interface Window {
        $crisp?: CrispClient;
        CRISP_WEBSITE_ID?: string;
    }
}

const hiddenRoutes = new Set(['/buzz/risk-chart', '/buzz/radar']);

function shouldHideCrisp(path: string): boolean {
    return hiddenRoutes.has(path)
        || path === '/support-lab'
        || path.startsWith('/support-lab/');
}

export default defineNuxtPlugin(() => {
    const store = usePersonStore();
    const route = useRoute();
    const user = store?.person?.emails.find((email) => email.primary)?.email;

    window.$crisp = [];
    window.$crisp.push(['safe', true]);
    window.CRISP_WEBSITE_ID = '117215d5-19e8-48b6-8ae0-e3ff96d6f5b8';

    function updateCrispVisibility(): void {
        window.$crisp?.push([
            'do',
            shouldHideCrisp(route.path) ? 'chat:hide' : 'chat:show',
        ]);
    }

    updateCrispVisibility();
    watch(() => route.path, updateCrispVisibility);

    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    document.head.appendChild(script);

    // Set user email when script is loaded
    script.onload = () => {
        if (user) {
            window.$crisp?.push(['set', 'user:email', user]);
        }

        // Create style element for position adjustment
        const style = document.createElement('style');
        style.id = 'crisp-mobile-position';
        document.head.appendChild(style);

        // Function to update Crisp position based on route
        const updateCrispPosition = () => {
            if (route.path === '/data-brokers') {
                style.textContent = `
                    @media (max-width: 768px) {
                        .cc-yv368[data-full-view=true] .cc-1kr6o .cc-18ov6 {
                            bottom: 50px !important;
                        }
                        .cc-18ov6 {
                            bottom: 50px !important;
                        }
                    }
                `;
            } else {
                style.textContent = '';
            }
        };

        // Initial position update
        updateCrispPosition();

        // Watch for route changes and update position
        watch(() => route.path, () => {
            updateCrispPosition();
        });

        // Add keyboard accessibility to Crisp chat widget
        setTimeout(() => {
            const crispWidget = document.querySelector('.crisp-client') as HTMLElement;
            if (crispWidget) {
                // Make the widget focusable
                crispWidget.setAttribute('tabindex', '0');
                crispWidget.setAttribute('role', 'button');
                crispWidget.setAttribute('aria-label', 'Open chat support');

                // Add keyboard event listeners
                crispWidget.addEventListener('keydown', (event: KeyboardEvent) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        // Open Crisp chat
                        window.$crisp?.push(['do', 'chat:open']);
                    } else if (event.key === 'Escape') {
                        event.preventDefault();
                        // Close Crisp chat
                        window.$crisp?.push(['do', 'chat:close']);
                    }
                });

                // Add focus styling
                crispWidget.style.outline = 'none';
                crispWidget.addEventListener('focus', () => {
                    crispWidget.style.boxShadow = '0 0 0 2px #3b82f6';
                });
                crispWidget.addEventListener('blur', () => {
                    crispWidget.style.boxShadow = 'none';
                });
            }
        }, 1000); // Wait for Crisp to fully load
    };
});
